import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryParams, NoSidebarPageLayout } from '@insurancemenu/imenu-components-lib';
import { SUBSCRIBER_CARD_DATA } from 'global/checklists-helpers/SubscriberChecklistHelper';
import ManageSubscriberContent from 'components/subscriber/manage-subscriber';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import ElectionSavingStatus from 'components/common/election/ElectionSavingStatus';
import { buildGroupContextMoreInfoBlobProps } from 'components/common/group/GroupContextMoreInfoBlob';
import { buildGroupContactInfoMap } from 'global/services-helpers/groupService';
import { isSubscriberValid } from 'global/utils/election/subscriberValidation';
import { toJS } from 'mobx';
import { HelpfulLinkStyled, SubtitleStyled, SubtitleWrapperStyled } from './styles';

// TESTING_POLICY: CYPRESS_ONLY

const ManageSubscriberPage: React.FC = observer(() => {
  const { periodId } = useParams<{ periodId: string }>();

  const query = useQueryParams();
  const subscriberId = query.get('subId') || '';
  const groupId = query.get('groupId') || '';

  const {
    manageSubscriberStore: {
      savingStatus,
      group,
      loading,
      requiredSubInfoPresenter,
      isStepCompleted,
      subscriber,
      getInitialData,
      completeElectionChangeStatus,
      updateElectionChanges,
      onBackRedirect,
    },
  } = useRootStore();

  if (!subscriberId) return null;

  const convertedSubscriber = subscriber && toJS(subscriber);
  const convertedRequiredSubInfo = requiredSubInfoPresenter ? toJS(requiredSubInfoPresenter) : undefined;
  const isCompleteButtonDisabled =
    !convertedSubscriber ||
    !convertedRequiredSubInfo ||
    !isSubscriberValid(convertedRequiredSubInfo, convertedSubscriber);

  useEffect(() => {
    void getInitialData(periodId, subscriberId, groupId);
  }, []);

  const onSaveStep = async (): Promise<void> => {
    if (!convertedSubscriber) return;
    await completeElectionChangeStatus(convertedSubscriber.id);
    onBackRedirect();
  };

  return (
    <NoSidebarPageLayout
      contentWidth="standard"
      isInitialLoading={loading}
      isInfoSectionLoading={loading}
      moreInfoBlob={buildGroupContextMoreInfoBlobProps({
        blobTitle: group?.name || '',
        generalInfoMap: { group: group?.name || '' },
        contactInfoMap: buildGroupContactInfoMap(group?.contactInfo ?? null),
      })}
      pageHeader={{
        subtitleMaxWidth: '65rem',
        title: SUBSCRIBER_CARD_DATA.MANAGE_SUBSCRIBER.title,
        subtitle: (
          <SubtitleWrapperStyled>
            <SubtitleStyled>
              Select or waive each benefit in the drop down. Details in each benefit will take you to an outline of
              coverage. You can also manage dependents and your employee profile. For help with this page, check out{' '}
              <HelpfulLinkStyled
                target="_blank"
                href="https://insurancemenu.zendesk.com/hc/en-us/articles/23905675387540-How-can-I-manage-my-benefits-on-InsuranceMenu"
                rel="noreferrer"
              >
                this tutorial
              </HelpfulLinkStyled>
              .
            </SubtitleStyled>
            {/* TODO Should be moved from header when new header spacing approach will be implemented */}
            <ElectionSavingStatus
              status={savingStatus}
              resave={(): void => {
                void updateElectionChanges();
              }}
            />
          </SubtitleWrapperStyled>
        ),
        onBackButtonClick: onBackRedirect,
        spacing: 'withBreadcrumbsSectionAndActionsBelow',
      }}
      pageFooter={{
        onBack: onBackRedirect,
        onComplete: isStepCompleted ? undefined : onSaveStep,
        completeButtonText: 'Save',
        disableCompleteButton: isCompleteButtonDisabled,
      }}
    >
      <ManageSubscriberContent />
    </NoSidebarPageLayout>
  );
});

export default ManageSubscriberPage;
